/**
 * MiniProjectTaskCard — compact list-row card for a single Mini-Project Task.
 *
 * DEV-1123 redesign (2026-05-26): replaces the prior 3-state pill cycle
 * with a binary checkbox + adds a polymorphic assignee chip. Description,
 * template/evidence badges, notes, and steps-count moved off the card
 * into the detail/edit tray (the card is the SCAN surface; the tray is
 * the CONSUME surface).
 *
 * 2026-05-27 layout pass (Skip's Schedule sub-tab feedback):
 *   Single horizontal row, two stacked text rows in the middle slot.
 *
 *     [☐]  Task name                                    May 30  [✎]
 *          Jane Doe · how-to-link
 *
 *   - Checkbox stays at the inline-start edge.
 *   - Name on top of the meta row; meta row carries assignee + optional
 *     how-to chip. (Skip didn't call out the how-to chip in the redesign
 *     but dropping it would lose information — it lives inline under the
 *     name where it's still scannable.)
 *   - Due date sits to the inline-start of the pencil.
 *   - Pencil is Lead-only (unchanged) and now lives at the inline-end
 *     edge per Skip's request — previously next to the name.
 *
 * Checkbox semantics — binary today, with `in_progress` derived later:
 *   - Click empty box → transition to status='done'
 *   - Click checked box → transition to status='not_started'
 *   - Backend column stays 3-state for API compat; this card only
 *     exposes two of those states. Multi-step "4/7" indicator will
 *     replace the "in_progress" surface when multi-step lands.
 *
 * Assignee chip resolution (Skip's "unassigned belongs to the lead" rule):
 *   - task.ownerUserGuid set → "Jane Doe" (user chip, neutral background)
 *   - task.assignedRoleGuid set → role name (purple background + shield)
 *   - Both null → "Lead Name (Lead default)" (italic + muted)
 *   The card receives team/roles/lead resolution data via props from
 *   TasksSection so the lookup happens once per render rather than per row.
 */

import React, { useCallback, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  transitionMiniProjectTask,
  type MiniProjectTask,
  type MiniProjectTeamMember,
} from "../../api/miniProjects";
import type { MiniProjectRole } from "../../api/miniProjectRoles";
import { useApiSecurityData } from "../../api/useApiSecurityData";
import { getCurrentUserGuid } from "../../api/client";
import styles from "./MiniProjectTaskCard.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

interface Props {
  miniProjectGuid: string;
  task: MiniProjectTask;
  isLead: boolean;
  /** Fired after a status transition succeeds. */
  onMutated: () => void;
  /** Fired when the Lead clicks the pencil to open the full edit tray. */
  onEdit: () => void;
  /** DEV-1288 — fired when the task body is clicked, to open the read/
   *  execution detail tray (everyone, not just the Lead). */
  onOpenDetail?: () => void;
  /** DEV-1123 — assignee chip resolution data. Provided by TasksSection
   *  which fetches once and shares across all card rows. */
  teamMembers?: MiniProjectTeamMember[];
  roles?: MiniProjectRole[];
}

/* ── Helpers ───────────────────────────────────────────────────── */

function formatDueDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function isOverdue(dueDate: string | null | undefined, status: string): boolean {
  if (!dueDate || status === "done") return false;
  try {
    const [y, m, d] = dueDate.split("-").map((s) => parseInt(s, 10));
    const due = new Date(y, (m || 1) - 1, d || 1);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due.getTime() < today.getTime();
  } catch {
    return false;
  }
}

/* ── Inline icons ──────────────────────────────────────────────── */

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

const LinkIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

/* ── Component ─────────────────────────────────────────────────── */

const MiniProjectTaskCard: React.FC<Props> = ({
  miniProjectGuid,
  task,
  isLead,
  onMutated,
  onEdit,
  onOpenDetail,
  teamMembers,
  roles,
}) => {
  const { t } = useAppTranslation("pages");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { securityUsers } = useApiSecurityData();

  const overdue = isOverdue(task.dueDate, task.status);
  const isDone = task.status === "done";

  /** Resolve a user GUID to display name via the global security cache. */
  const lookupUserName = useCallback((userGuid: string): string => {
    const rows = (securityUsers || []) as Array<Record<string, unknown>>;
    const target = userGuid.toUpperCase();
    const user = rows.find((u) => String(u.user_guid).toUpperCase() === target);
    return (
      (user?.display_name as string) ||
      (user?.name as string) ||
      (user?.screen_name as string) ||
      userGuid
    );
  }, [securityUsers]);

  /** DEV-1123 assignee chip resolution. Returns null when no chip should
   *  render (e.g., data not loaded yet AND nothing assigned). */
  const assigneeChip = useMemo(() => {
    if (task.ownerUserGuid) {
      return {
        kind: "user" as const,
        label: lookupUserName(task.ownerUserGuid),
      };
    }
    if (task.assignedRoleGuid && roles) {
      const role = roles.find((r) => r.roleGuid === task.assignedRoleGuid);
      if (role) {
        return { kind: "role" as const, label: role.name };
      }
    }
    // Unassigned: fall back to the Lead per "unassigned belongs to the lead."
    if (teamMembers) {
      const lead = teamMembers.find((tm) => tm.role === "lead");
      if (lead) {
        return {
          kind: "lead-default" as const,
          label: lookupUserName(lead.userGuid),
        };
      }
    }
    return null;
  }, [task.ownerUserGuid, task.assignedRoleGuid, roles, teamMembers, lookupUserName]);

  // DEV-1257-followup. Non-Lead assignees can act on their own tasks.
  // Mirrors backend CanUpdateOwnTaskStatus: direct OwnerUserGuid match
  // OR holding the AssignedRoleGuid via an active role assignment. The
  // checkbox + pencil were previously Lead-only, locking assignees out
  // of their own work — Skip flagged this directly.
  const canActOnTask = useMemo(() => {
    if (isLead) return true;
    const me = getCurrentUserGuid().toUpperCase();
    if (!me) return false;
    if (task.ownerUserGuid && task.ownerUserGuid.toUpperCase() === me) return true;
    if (task.assignedRoleGuid && roles) {
      const role = roles.find(
        (r) => r.roleGuid.toUpperCase() === task.assignedRoleGuid!.toUpperCase(),
      );
      if (
        role &&
        role.activeAssignees.some((a) => a.userGuid.toUpperCase() === me)
      ) {
        return true;
      }
    }
    return false;
  }, [isLead, task.ownerUserGuid, task.assignedRoleGuid, roles]);

  /** Toggle the binary checkbox. Sends a status transition matching the
   *  new state. Backend keeps 3-state column for API compat; the card
   *  exposes only `not_started` and `done`. */
  const handleCheckboxChange = useCallback(async () => {
    if (!canActOnTask || saving) return;
    setError(null);
    setSaving(true);
    try {
      const nextStatus = isDone ? "not_started" : "done";
      await transitionMiniProjectTask(miniProjectGuid, task.taskGuid, nextStatus);
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [canActOnTask, saving, isDone, miniProjectGuid, task.taskGuid, onMutated]);

  // Has-any-meta-content guard — only render the under-name meta line
  // when there's something to show. Avoids an empty 14px-tall row.
  const hasMeta = assigneeChip !== null || !!task.howToUrl;

  return (
    <div className={styles.card} data-status={task.status}>
      <div className={styles.row}>
        {/* Checkbox — inline-start edge. Disabled for non-Lead viewers. */}
        <input
          type="checkbox"
          className={styles.taskCheckbox}
          checked={isDone}
          onChange={handleCheckboxChange}
          disabled={!canActOnTask || saving}
          aria-label={isDone
            ? t("miniProject.cards.task.markNotDone")
            : t("miniProject.cards.task.markDone")}
          title={isDone
            ? t("miniProject.cards.task.markNotDone")
            : t("miniProject.cards.task.markDone")}
        />

        {/* Middle slot — name on top, assignee + how-to chip stacked below.
            Min-width:0 + overflow-hidden lets the name truncate cleanly
            when the row is narrow rather than pushing the right side
            off-screen. */}
        <div
          className={`${styles.titleBlock} ${onOpenDetail ? styles.titleBlockClickable : ""}`}
          {...(onOpenDetail
            ? {
                role: "button" as const,
                tabIndex: 0,
                onClick: onOpenDetail,
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenDetail(); }
                },
                title: t("taskDetail.openHint"),
              }
            : {})}
        >
          <span className={`${styles.name} ${isDone ? styles.nameDone : ""}`}>
            {/* System/template tasks carry a name_key; resolve it via t().
                i18next returns the key itself when it isn't a known locale key,
                so this stays backward-compatible with legacy template keys. */}
            {task.nameText || (task.nameKey && t(task.nameKey)) || t("miniProject.cards.task.noName")}
          </span>
          {hasMeta && (
            <div className={styles.metaLine}>
              {assigneeChip && (
                <span
                  className={`${styles.assignee} ${
                    assigneeChip.kind === "role"
                      ? styles.assigneeRole
                      : assigneeChip.kind === "lead-default"
                        ? styles.assigneeLeadDefault
                        : ""
                  }`}
                  title={
                    assigneeChip.kind === "lead-default"
                      ? t("miniProject.cards.task.assigneeLeadDefaultHint")
                      : undefined
                  }
                >
                  {assigneeChip.kind === "role" && <ShieldIcon />}
                  {assigneeChip.label}
                  {assigneeChip.kind === "lead-default" && (
                    <span className={styles.assigneeSuffix}>
                      {" "}
                      {t("miniProject.cards.task.assigneeLeadDefaultSuffix")}
                    </span>
                  )}
                </span>
              )}
              {task.howToUrl && (
                <a
                  href={task.howToUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.linkChip}
                  onClick={(e) => e.stopPropagation()}
                >
                  <LinkIcon />
                  {t("miniProject.cards.task.howToChip")}
                </a>
              )}
            </div>
          )}
        </div>

        {/* Due date — sits just to the inline-start of the pencil per
            Skip's 2026-05-27 layout pass. Hidden when unset. */}
        {task.dueDate && (
          <span
            className={`${styles.due} ${overdue ? styles.dueOverdue : ""}`}
          >
            {formatDueDate(task.dueDate)}
          </span>
        )}

        {/* Pencil opens the detail tray. Available to Leads (full edit)
            and to assignees (view + status cycle; structural fields gated
            inside the tray on isLead). DEV-1257-followup. */}
        {canActOnTask && (
          <button
            type="button"
            className={styles.editBtn}
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            aria-label={t("miniProject.cards.task.editAria")}
            title={t("miniProject.cards.task.editTitle")}
          >
            <PencilIcon />
          </button>
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

export default MiniProjectTaskCard;
